import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useEffect, useRef, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { useMyOrg } from "@/hooks/use-my-org";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import {
  compareFacilityServiceRetention,
  type ServiceRetentionInsightsResponse,
} from "@/libs/analyticsApi";

const completedEightWeekWindow = () => {
  const currentMonday = new Date();
  currentMonday.setUTCHours(0, 0, 0, 0);
  currentMonday.setUTCDate(
    currentMonday.getUTCDate() - ((currentMonday.getUTCDay() + 6) % 7),
  );
  const anchor = Date.UTC(1970, 0, 5);
  const elapsedDays = Math.floor(
    (currentMonday.getTime() - anchor) / 86_400_000,
  );
  const endBoundary = new Date(currentMonday);
  endBoundary.setUTCDate(endBoundary.getUTCDate() - (elapsedDays % 56));
  const start = new Date(endBoundary);
  start.setUTCDate(start.getUTCDate() - 56);
  const lastIncluded = new Date(endBoundary);
  lastIncluded.setUTCDate(lastIncluded.getUTCDate() - 1);
  return {
    start: start.toISOString().slice(0, 10),
    end: lastIncluded.toISOString().slice(0, 10),
  };
};
const asInstant = (date: string) => `${date}T00:00:00Z`;
const afterInclusiveDate = (date: string) => {
  const end = new Date(asInstant(date));
  end.setUTCDate(end.getUTCDate() + 1);
  return end.toISOString();
};
const dateProps = {
  fullWidth: true,
  size: "small" as const,
  slotProps: { inputLabel: { shrink: true } },
  type: "date",
};

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <Box>
      <Typography
        sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 800 }}
        variant="h5"
      >
        {value}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {label}
      </Typography>
    </Box>
  );
}

export function ServiceRetentionInsightsWorkspace() {
  const { user } = useAuth();
  const orgView = useMyOrg();
  const { facility, facilityId, isLoading: facilityLoading } =
    useResolvedFacility();
  const initial = useRef(completedEightWeekWindow()).current;
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [result, setResult] =
    useState<ServiceRetentionInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<
    "unavailable" | "forbidden" | "invalid" | "retry" | null
  >(null);
  const generation = useRef(0);
  const activeFacilityId = useRef(facilityId);
  activeFacilityId.current = facilityId;
  const organizationOwner = Boolean(
    user?.uid && orgView.org?.owner_user_id === user.uid,
  );

  useEffect(() => {
    generation.current += 1;
    setResult(null);
    setError(null);
    setLoading(false);
  }, [facilityId]);

  const editWindow = (setter: (value: string) => void, value: string) => {
    generation.current += 1;
    setter(value);
    setResult(null);
    setError(null);
  };

  const load = async () => {
    if (!facilityId || loading) return;
    const requestFacility = facilityId;
    const requestGeneration = ++generation.current;
    const current = () =>
      generation.current === requestGeneration &&
      activeFacilityId.current === requestFacility;
    setLoading(true);
    setError(null);
    try {
      const response = await compareFacilityServiceRetention(requestFacility, {
        start: asInstant(start),
        end: afterInclusiveDate(end),
      });
      if (!current()) return;
      if (response.facility_id !== requestFacility) {
        setError("retry");
        return;
      }
      setResult(response);
    } catch (cause) {
      if (!current()) return;
      setResult(null);
      if (isApiError(cause) && cause.status === 503) setError("unavailable");
      else if (isApiError(cause) && cause.status === 403) setError("forbidden");
      else if (isApiError(cause) && cause.status === 422) setError("invalid");
      else setError("retry");
    } finally {
      if (current()) setLoading(false);
    }
  };

  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading service retention"
        role="status"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "center", minHeight: "40vh" }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  const messages = {
    unavailable:
      "Service retention isn’t enabled for this organization. Baseline attendance, check-ins, memberships, registrations, receipts, and exports remain available.",
    forbidden: "You don’t have permission to view internal service retention.",
    invalid: "Choose one canonical 8-week UTC period, Monday through Sunday.",
    retry: "We couldn’t build this retention report. Review the dates and try again.",
  };

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 3 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{ alignItems: { sm: "center" }, justifyContent: "space-between" }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 800 }} variant="h5">
            Service retention
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Compare repeat participation by service at {facility?.name ?? "this facility"}.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Button
            component={RouterLink}
            size="small"
            to="/analytics/insights/growth"
            variant="outlined"
          >
            Customers &amp; campaigns
          </Button>
          <Chip
            color="primary"
            icon={<ReplayOutlinedIcon />}
            label="Insights module"
            variant="outlined"
          />
        </Stack>
      </Stack>

      <Paper
        component="form"
        onSubmit={(event) => {
          event.preventDefault();
          void load();
        }}
        sx={{ p: { xs: 2, sm: 2.5 } }}
        variant="outlined"
      >
        <Stack spacing={2}>
          <Box>
            <Typography component="h2" sx={{ fontWeight: 700 }} variant="h6">
              Select a completed period
            </Typography>
            <Typography color="text.secondary" variant="body2">
              Select a canonical 8-week UTC period. Each service compares its
              participants with the immediately preceding non-overlapping 8 weeks.
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
            <TextField
              {...dateProps}
              label="Start date"
              onChange={(event) => editWindow(setStart, event.target.value)}
              value={start}
            />
            <TextField
              {...dateProps}
              label="Last included day"
              onChange={(event) => editWindow(setEnd, event.target.value)}
              value={end}
            />
          </Stack>
          <Button
            disabled={loading}
            sx={{ alignSelf: "flex-start" }}
            type="submit"
            variant="contained"
          >
            {loading ? "Building report…" : "Build retention report"}
          </Button>
        </Stack>
      </Paper>

      {error ? (
        <Alert
          action={
            error === "unavailable" && organizationOwner ? (
              <Button
                color="inherit"
                component={RouterLink}
                size="small"
                to="/settings/contract"
              >
                Review modules
              </Button>
            ) : error === "retry" ? (
              <Button color="inherit" onClick={() => void load()} size="small">
                Retry
              </Button>
            ) : undefined
          }
          severity={error === "unavailable" ? "info" : "error"}
        >
          {messages[error]}
        </Alert>
      ) : null}

      {result ? (
        <Stack spacing={2.5}>
          <Box>
            <Typography color="text.secondary" variant="body2">
              {result.decision_supported}
            </Typography>
            <Typography color="text.secondary" variant="caption">
              {result.freshness}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(2, minmax(0, 1fr))",
              },
            }}
          >
            {result.signals.map((signal) => (
              <Paper
                component="article"
                key={signal.service}
                sx={{ p: { xs: 2, sm: 2.5 } }}
                variant="outlined"
              >
                <Stack spacing={2}>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }} variant="h6">
                      {signal.label}
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      {signal.definition}
                    </Typography>
                  </Box>
                  <LinearProgress
                    aria-label={`${signal.label} return rate`}
                    value={signal.return_rate_percent}
                    variant="determinate"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gap: 2,
                      gridTemplateColumns: {
                        xs: "repeat(2, minmax(0, 1fr))",
                        sm: "repeat(4, minmax(0, 1fr))",
                      },
                    }}
                  >
                    <Metric label="Reference" value={signal.reference_customers} />
                    <Metric label="Current" value={signal.focus_customers} />
                    <Metric label="Returned" value={signal.returning_customers} />
                    <Metric label="Return rate" value={`${signal.return_rate_percent}%`} />
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>
          {result.suppressed_services.length ? (
            <Alert severity="info">
              {result.suppressed_services.length} service
              {result.suppressed_services.length === 1 ? " is" : "s are"} hidden
              because at least one cohort is below the privacy minimum of{" "}
              {result.cohort_minimum}.
            </Alert>
          ) : null}
          {!result.signals.length ? (
            <Alert severity="info">
              No service has a reportable repeat-participation cohort for this period.
            </Alert>
          ) : null}
          <Paper sx={{ p: 2 }} variant="outlined">
            <Typography sx={{ fontWeight: 700 }} variant="subtitle2">
              How to read this report
            </Typography>
            <Stack
              component="ul"
              spacing={0.75}
              sx={{ color: "text.secondary", mb: 0, mt: 1, pl: 2.5 }}
            >
              {result.limitations.map((limitation) => (
                <Typography component="li" key={limitation} variant="body2">
                  {limitation}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Stack>
      ) : null}
    </Stack>
  );
}
